import { FC, useEffect } from 'react'
import { LogLevel, User } from 'src/common-services'
import { loggingClient } from 'src/clients'
import { browserLogger } from 'src/utils/browser-logger'
import { useRouter } from 'next/router'
import { useCachedRecoilValue } from 'src/utils/recoil'
import { currentUser } from 'src/atoms/user'
import * as smartlook from 'src/utils/smartlook'
import { initSessionReplay } from 'src/utils/amplitude'
import { format } from 'date-fns'
import { getDateWithoutBloodyTZ, getDateWithoutBloodyTZFromDate } from 'src/utils/date-utils'

type Profile = Record<string, string | number | Date>

const init = () => {
  // server-side rendering
  if (typeof window === 'undefined') {
    return {
      setUserId: () => {},
      setPagePath: () => {},
      logEvent: () => {},
      getUser: () => {},
      addSourceMiddleware: () => {},
    }
  }

  // gtag wont be around in dev, so just log to terminal to test how things will be loggedd
  if (!window.gtag) {
    window.gtag = (...args: any[]) => {
      browserLogger.debug('analytics', ...args)
    }
  }

  const win = window as any

  win.analytics =
    win.analytics ||
    // debug analytics calls if analytics isn't loaded (local/dev)
    new Proxy(
      {},
      {
        get:
          (target, name) =>
          (...args: any[]) => {
            browserLogger.debug(`called ${String(name)} with ${JSON.stringify(args)}`)
          },
      }
    )

  const gaid: string = (window as any).GA_MEASUREMENT_ID || ''
  return {
    setUserId(id: User['id'], email: User['email'], profile: Profile) {
      win.analytics.identify(email, { email, ...profile })
      // https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id#set_user_id
      window.gtag('config', gaid, { user_id: id })
    },
    setPagePath(path: string) {
      // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
      window.gtag('config', gaid, { page_path: path })
      win.analytics.page(path)
    },
    logEvent(eventName: string, metadata?: Record<string, any>) {
      // instance.logEvent(eventName, metadata)
      win.analytics.track(eventName, metadata)
    },
    getUser() {
      return win.analytics.user()
    },
    addSourceMiddleware: win.analytics.addSourceMiddleware,
  }
}

const tracker = init()

const logEventTracker = tracker.logEvent.bind(tracker)

export const logEvent = (eventName: string, metadata?: Record<string, any> | undefined): void => {
  const isProd = window.location.toString().includes('alphamap.com')
  if (!isProd) {
    browserLogger.debug('would log', eventName, metadata)
    return
  }
  logEventTracker(eventName, metadata)
  void loggingClient.logData({
    level: LogLevel.INFO,
    message: `ui-event-${eventName}`,
    data: {
      ...metadata,
    },
  })
}

export const Analytics: FC = () => {
  const currentUserMaybe = useCachedRecoilValue(currentUser, undefined)
  const { asPath } = useRouter()
  const location = asPath.split('?')[0].split('/').slice(0, 3).join('/')

  useEffect(() => {
    if (!currentUserMaybe) {
      return
    }

    // NOTE(bmc): all this checking for nulls and stuff should get better when we
    // remove systemMeta from the user object & make all these fields always exist.
    // for now, its just ugly but i don't wanna throw an exception just to log some stuff
    let firstName = ''
    let lastName = ''
    let company = ''
    try {
      if (currentUserMaybe) {
        firstName = currentUserMaybe?.firstName ?? ''
        lastName = currentUserMaybe?.lastName ?? ''
      }
      company = currentUserMaybe?.company?.name ?? ''
    } catch (e) {
      browserLogger.error(e)
    }

    const profile: Profile = {
      role: currentUserMaybe.role,
      firstName,
      lastName,
      company,
    }
    try {
      if (currentUserMaybe.lastLogin) {
        let date: Date | undefined = undefined

        if (typeof currentUserMaybe.lastLogin === 'string') {
          date = getDateWithoutBloodyTZ(currentUserMaybe.lastLogin)
        } else {
          date = getDateWithoutBloodyTZFromDate(currentUserMaybe.lastLogin)
        }

        if (date) {
          profile.lastLogin = format(date, 'MMMM d, yyyy hh:mm:ss.SSS a')
        }
      }
    } catch (e) {
      browserLogger.error(e)
    }

    tracker.setUserId(currentUserMaybe.id, currentUserMaybe.email, profile)
    smartlook.initSmartlook()
    smartlook.identifyUser(currentUserMaybe)
    logEvent('page_load')
  }, [Boolean(currentUserMaybe)])

  useEffect(() => {
    void initSessionReplay()
  }, [])

  useEffect(() => {
    tracker.setPagePath(location)
  }, [location])

  return null
}
